import { alpha, createTheme, type PaletteMode } from '@mui/material';
import { brand, lightColors, darkColors } from '@rekindle/shared/theme';

/** One presentation owner for resource grids, operational tables and forms. */
function workspaceTheme(mode: PaletteMode) {
  const colors = mode === 'light' ? lightColors : darkColors;
  const primary = mode === 'dark' ? brand.primaryLight : brand.primary;
  return createTheme({
    palette: {
      mode, primary: { main: primary }, secondary: { main: brand.secondary },
      background: { default: colors.background, paper: colors.surface },
      text: { primary: colors.text, secondary: colors.textSecondary }, divider: colors.border,
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: 'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h4: { fontSize: '1.875rem', fontWeight: 750, letterSpacing: '-0.04em' },
      h5: { fontSize: '1.3rem', fontWeight: 700, letterSpacing: '-0.025em' },
      h6: { fontSize: '1rem', fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 650, letterSpacing: 0 },
      body2: { lineHeight: 1.6 },
    },
    sidebar: { width: 256, closedWidth: 68 },
    components: {
      MuiCssBaseline: { styleOverrides: {
        ':focus-visible': { outline: `2px solid ${primary}`, outlineOffset: 3 },
        '@media (prefers-reduced-motion: reduce)': { '*, *::before, *::after': { transitionDuration: '0.01ms !important', animationDuration: '0.01ms !important' } },
      } },
      MuiButton: { defaultProps: { disableElevation: true }, styleOverrides: { root: { borderRadius: 9, minHeight: 36, paddingInline: 14 } } },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 9 } } },
      MuiCard: { defaultProps: { elevation: 0 }, styleOverrides: { root: { border: `1px solid ${colors.border}`, backgroundImage: 'none', boxShadow: `0 2px 8px ${alpha(colors.text, 0.025)}` } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      MuiChip: { styleOverrides: { root: { borderRadius: 7, fontWeight: 600, fontSize: '0.75rem' } } },
      MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 9, backgroundColor: colors.surface } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 16 } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiTableCell: { styleOverrides: {
        root: { borderBottomColor: colors.border, padding: '12px 16px', fontSize: '0.8125rem' },
        head: { backgroundColor: mode === 'light' ? alpha(brand.primary, 0.035) : alpha(colors.text, 0.045), color: colors.textSecondary, fontWeight: 700, whiteSpace: 'nowrap', fontSize: '0.75rem' },
      } },
      MuiTableRow: { styleOverrides: { root: { '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: alpha(brand.primary, 0.08) }, '&.MuiTableRow-hover:hover': { backgroundColor: alpha(brand.primary, 0.035) } } } },
      MuiTablePagination: { styleOverrides: { toolbar: { minHeight: 60, flexWrap: 'wrap' } } },
      RaLayout: { styleOverrides: { root: {
        minWidth: 0,
        '& .RaLayout-contentWithSidebar': { minWidth: 0 },
        '& .RaLayout-content': { minWidth: 0, padding: '0 28px 32px', '@media (max-width: 600px)': { padding: '0 12px 24px' } },
      } } },
      RaSidebar: { styleOverrides: { root: { '& .MuiDrawer-paper': { backgroundColor: colors.surface, borderRight: `1px solid ${colors.border}` } } } },
      RaAppBar: { styleOverrides: { root: { borderBottom: `1px solid ${colors.border}`, backgroundColor: colors.surface, color: colors.text } } },
      RaMenuItemLink: { styleOverrides: { root: { margin: '2px 0', borderRadius: 9, minHeight: 40, fontSize: '0.875rem', color: colors.textSecondary,
        '&.RaMenuItemLink-active': { color: primary, backgroundColor: alpha(primary, 0.09), fontWeight: 700 },
        '& .MuiListItemIcon-root': { minWidth: 36, color: 'inherit' },
      } } },
      RaList: { styleOverrides: { root: { '& .RaList-main': { minWidth: 0 }, '& .RaList-content': { borderRadius: 12 } } } },
      RaListToolbar: { styleOverrides: { root: { alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12,
        '& > span:empty': { display: 'none' },
        '& > form': { flex: '1 1 240px', minWidth: 0 },
        '& .MuiToolbar-root': { flex: '0 0 auto', minHeight: 44, padding: 0 },
        '@media (max-width: 600px)': { flexDirection: 'column', flexWrap: 'nowrap', alignItems: 'stretch', backgroundColor: 'transparent',
          '& > form': { flex: '0 0 auto' }, '& .MuiToolbar-root': { width: '100%', justifyContent: 'flex-start', backgroundColor: 'transparent', flexWrap: 'wrap' },
        },
      } } },
      RaTopToolbar: { styleOverrides: { root: { padding: 0, minHeight: 44, gap: 4, flexWrap: 'wrap', alignItems: 'center' } } },
      RaFilterForm: { styleOverrides: { root: { gap: 8, alignItems: 'center', minHeight: 44, flex: '1 1 240px', padding: 0,
        '& .RaFilterForm-filterFormInput .MuiFormControl-root': { marginTop: 0 },
        '@media (max-width: 600px)': { flex: '0 0 auto' },
      } } },
    },
  });
}
export const lightTheme = workspaceTheme('light');
export const darkTheme = workspaceTheme('dark');
